import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Crown, Trophy, History, Users, Share2, X, Palette, Building2 } from 'lucide-react'
import { useTranslation } from '../hooks/use-i18n'
import { ArenaPlayerCard, ArenaGameCard, ArenaWaitlist } from '../components/arena'
import { CHESSARENAS_URL } from '../constants/chessProduct'

const MOCK_LEADERBOARD = [
  { id: '1', name: 'Maria Garcia', points: 12.5, wins: 10, draws: 5, losses: 2 },
  { id: '2', name: 'Carlos Ruiz', points: 11, wins: 9, draws: 4, losses: 3 },
  { id: '3', name: 'Ana Lopez', points: 9.5, wins: 8, draws: 3, losses: 4 },
  { id: '4', name: 'Pablo Fernandez', points: 7, wins: 6, draws: 2, losses: 5 },
  { id: '5', name: 'Laura Martinez', points: 4.5, wins: 4, draws: 1, losses: 6 },
]

const MOCK_WAITLIST = [
  { id: 'w1', name: 'Sofia Torres', rating: 1680 },
  { id: 'w2', name: 'Diego Sanchez', rating: 1520 },
  { id: 'w3', name: 'Elena Ramirez', rating: 1750 },
]

const MOCK_RECENT_GAMES = [
  { id: 'g1', whitePlayer: 'Maria Garcia', blackPlayer: 'Carlos Ruiz', result: 'white' as const, date: 'Mar 10, 14:30', moves: 42 },
  { id: 'g2', whitePlayer: 'Ana Lopez', blackPlayer: 'Pablo Fernandez', result: 'draw' as const, date: 'Mar 10, 12:15', moves: 68 },
  { id: 'g3', whitePlayer: 'Carlos Ruiz', blackPlayer: 'Laura Martinez', result: 'black' as const, date: 'Mar 9, 18:00', moves: 55 },
]

const Arena: React.FC = () => {
  const { t } = useTranslation()
  const [customizeModalOpen, setCustomizeModalOpen] = useState(true)
  const [leftTab, setLeftTab] = useState<'rankings' | 'recent'>('rankings')

  useEffect(() => {
    const closed = sessionStorage.getItem('arena-demo-customize-closed')
    if (closed === '1') setCustomizeModalOpen(false)
  }, [])

  const closeCustomizeModal = () => {
    setCustomizeModalOpen(false)
    sessionStorage.setItem('arena-demo-customize-closed', '1')
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Customize arena modal - modern CTA */}
      {customizeModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-md"
          onClick={closeCustomizeModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="customize-modal-title"
        >
          <div
            className="relative max-w-lg w-full rounded-3xl border border-primary/30 bg-card/95 backdrop-blur-xl shadow-2xl shadow-primary/10 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top accent */}
            <div className="h-1.5 bg-gradient-to-r from-primary via-chess to-primary" />
            <div className="p-6 sm:p-8">
              <button
                type="button"
                onClick={closeCustomizeModal}
                className="absolute top-4 right-4 p-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="flex justify-center mb-5">
                <div className="w-16 h-16 rounded-2xl bg-primary/15 border border-primary/20 flex items-center justify-center">
                  <Palette className="w-8 h-8 text-primary" />
                </div>
              </div>
              <h2 id="customize-modal-title" className="font-display text-xl sm:text-2xl font-bold text-foreground text-center mb-2">
                {t('arena.customizeModalTitle')}
              </h2>
              <p className="text-muted-foreground text-center text-sm sm:text-base leading-relaxed mb-6">
                {t('arena.customizeModalText')}
              </p>
              <div className="flex flex-col-reverse sm:flex-row gap-3 justify-center items-stretch sm:items-center">
                <button
                  type="button"
                  onClick={closeCustomizeModal}
                  className="px-5 py-3 rounded-xl border border-border text-sm font-medium text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors"
                >
                  {t('arena.customizeModalClose')}
                </button>
                <Link
                  to="/company-registration"
                  onClick={closeCustomizeModal}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity shadow-lg shadow-primary/25"
                >
                  <Building2 className="w-4 h-4" />
                  {t('arena.customizeModalCta')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Demo header */}
      <header className="sticky top-14 z-30 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="max-w-6xl xl:max-w-7xl mx-auto px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-xl font-bold text-foreground">
              <Crown className="w-6 h-6 text-amber-500" />
              {t('arena.demoTitle')}
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">{t('arena.demoTagline')}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {t('arena.seatsOpen')} {t('arena.waitlistDisplayOnly')}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <a
              href={CHESSARENAS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 rounded-lg bg-amber-500 text-amber-950 text-sm font-semibold hover:opacity-90 inline-flex items-center justify-center gap-1.5 text-center"
            >
              {t('arena.playOnChessArenas')} →
            </a>
            <a
              href={CHESSARENAS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 rounded-lg border border-border bg-muted/50 text-sm font-medium hover:bg-muted transition-colors text-center"
            >
              chessarenas.com
            </a>
          </div>
        </div>
      </header>

      {/* Play on ChessArenas.com — developed by Break Room Cup */}
      <div className="border-b border-primary/20 bg-primary/10">
        <div className="max-w-6xl xl:max-w-7xl mx-auto px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-foreground">
            <span className="font-semibold">{t('arena.chessArenasBannerTitle')}</span>
            {' '}
            <span className="text-muted-foreground">{t('arena.chessArenasBannerText')}</span>
          </p>
          <a
            href={CHESSARENAS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 inline-flex items-center justify-center px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90"
          >
            {t('arena.playOnChessArenas')}
          </a>
        </div>
      </div>

      {/* Demo dashboard: same layout as ChessArenaRoom */}
      <div className="container max-w-6xl xl:max-w-7xl mx-auto p-4 flex flex-col xl:flex-row gap-4 xl:gap-6">
        {/* Left: Rankings | Recent */}
        <aside className="hidden xl:block xl:w-72 xl:order-1 xl:shrink-0">
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="flex border-b border-border p-1 gap-0">
              <button
                type="button"
                onClick={() => setLeftTab('rankings')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium rounded-md ${leftTab === 'rankings' ? 'bg-muted' : ''}`}
              >
                <Trophy className="w-3.5 h-3.5" /> {t('chessArena.rankings')}
              </button>
              <button
                type="button"
                onClick={() => setLeftTab('recent')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium rounded-md ${leftTab === 'recent' ? 'bg-muted' : ''}`}
              >
                <History className="w-3.5 h-3.5" /> {t('chessArena.recent')}
              </button>
            </div>
            <div className="p-2 max-h-[60vh] overflow-y-auto">
              {leftTab === 'rankings' &&
                MOCK_LEADERBOARD.map((entry, idx) => (
                  <div key={entry.id} className="mb-2">
                    <ArenaPlayerCard
                      name={entry.name}
                      rank={idx + 1}
                      points={entry.points}
                      wins={entry.wins}
                      draws={entry.draws}
                      losses={entry.losses}
                    />
                  </div>
                ))}
              {leftTab === 'recent' &&
                MOCK_RECENT_GAMES.map((g) => (
                  <div key={g.id} className="mb-2">
                    <ArenaGameCard
                      whitePlayer={g.whitePlayer}
                      blackPlayer={g.blackPlayer}
                      result={g.result}
                      date={g.date}
                      moves={g.moves}
                    />
                  </div>
                ))}
            </div>
          </div>
        </aside>

        {/* Center: board (blurred demo) + seats */}
        <div className="flex-1 xl:order-2 xl:max-w-[480px] xl:mx-auto w-full">
          <div className="flex flex-col items-center gap-1 sm:gap-2 max-w-[400px] mx-auto">
            {/* Black seat - sign in */}
            <div className="w-full p-3 rounded-xl border-2 border-border border-dashed bg-muted/50">
              <div className="text-center py-1">
                <p className="text-sm text-muted-foreground">
                  Black seat –{' '}
                  <a href={CHESSARENAS_URL} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    {t('arena.playOnChessArenas')}
                  </a>
                </p>
              </div>
            </div>

            {/* Board: real chess board image behind waiting message */}
            <div className="relative w-full bg-[#302e2b] rounded-lg overflow-hidden" style={{ aspectRatio: '1' }}>
              {/* 8x8 chess board background */}
              <div className="absolute inset-0 opacity-90">
                <svg viewBox="0 0 8 8" className="w-full h-full" preserveAspectRatio="none">
                  {[0, 1, 2, 3, 4, 5, 6, 7].map((row) =>
                    [0, 1, 2, 3, 4, 5, 6, 7].map((col) => (
                      <rect
                        key={`${row}-${col}`}
                        x={col}
                        y={row}
                        width={1}
                        height={1}
                        fill={(row + col) % 2 === 0 ? '#f0d9b5' : '#b58863'}
                      />
                    ))
                  )}
                </svg>
              </div>
              <div className="absolute inset-0 flex flex-col items-center justify-center z-10 bg-background/60 backdrop-blur-[2px] text-foreground p-4 text-center">
                <p className="text-base font-semibold mb-1">{t('arena.demoWaitingForPlayers')}</p>
                <p className="text-sm text-muted-foreground mb-3">{t('arena.demoTakeSeat')}</p>
                <a
                  href={CHESSARENAS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 rounded-lg bg-amber-500 text-amber-950 text-sm font-semibold hover:opacity-90"
                >
                  {t('arena.playOnChessArenas')} →
                </a>
              </div>
            </div>

            {/* White seat - sign in */}
            <div className="w-full p-3 rounded-xl border-2 border-border border-dashed bg-muted/50">
              <div className="text-center py-1">
                <p className="text-sm text-muted-foreground">
                  White seat –{' '}
                  <a href={CHESSARENAS_URL} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    {t('arena.playOnChessArenas')}
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Waitlist, Arena Info, Copy Link */}
        <aside className="xl:w-72 xl:order-3 xl:shrink-0 space-y-4">
          <ArenaWaitlist
            players={MOCK_WAITLIST}
            isGameOngoing={false}
            isUserInWaitlist={false}
            onJoinWaitlist={() => {}}
            onLeaveWaitlist={() => {}}
            isAuthenticated={false}
          />
          <div className="rounded-xl border border-border bg-card p-3 sm:p-4">
            <div className="flex items-center gap-1.5 sm:gap-2 mb-3">
              <Users className="w-4 h-4 text-primary" />
              <h3 className="font-semibold text-sm sm:text-base">Arena Info</h3>
            </div>
            <ul className="text-sm space-y-1.5 text-muted-foreground">
              <li><span className="font-medium text-foreground">Status:</span> Waiting</li>
              <li><span className="font-medium text-foreground">Waitlist:</span> 3 players</li>
              <li><span className="font-medium text-foreground">Games:</span> 3</li>
            </ul>
          </div>
          <div>
            <p className="text-xs mb-2 font-medium text-muted-foreground">{t('chessArena.shareThisArena')}</p>
            <button
              type="button"
              className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-border hover:bg-muted text-xs font-medium"
            >
              <Share2 className="w-3 h-3" /> {t('chessArena.copyLink')}
            </button>
          </div>
        </aside>
      </div>

      {/* Mobile: show rankings below center on small screens */}
      <div className="xl:hidden mt-6 px-4">
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="flex border-b border-border p-1">
            <button
              type="button"
              onClick={() => setLeftTab('rankings')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium rounded-md ${leftTab === 'rankings' ? 'bg-muted' : ''}`}
            >
              <Trophy className="w-3.5 h-3.5" /> {t('chessArena.rankings')}
            </button>
            <button
              type="button"
              onClick={() => setLeftTab('recent')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium rounded-md ${leftTab === 'recent' ? 'bg-muted' : ''}`}
            >
              <History className="w-3.5 h-3.5" /> {t('chessArena.recent')}
            </button>
          </div>
          <div className="p-2 max-h-[40vh] overflow-y-auto">
            {leftTab === 'rankings' &&
              MOCK_LEADERBOARD.map((entry, idx) => (
                <div key={entry.id} className="mb-2">
                  <ArenaPlayerCard
                    name={entry.name}
                    rank={idx + 1}
                    points={entry.points}
                    wins={entry.wins}
                    draws={entry.draws}
                    losses={entry.losses}
                  />
                </div>
              ))}
            {leftTab === 'recent' &&
              MOCK_RECENT_GAMES.map((g) => (
                <div key={g.id} className="mb-2">
                  <ArenaGameCard
                    whitePlayer={g.whitePlayer}
                    blackPlayer={g.blackPlayer}
                    result={g.result}
                    date={g.date}
                    moves={g.moves}
                  />
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Arena
